import React, { FC } from 'react'
import { CellularSavedBankAccount, CellularSavedCard, PaymentMethodChoice } from './types'
import { useFormStyles } from './form-styles'
import { formatCardExpiry, formatSavedBankAccount, formatSavedCard } from './format'

interface PaymentMethodOptionsProps {
  savedCard?: CellularSavedCard | null
  // A saved bank account and a new one are offered only when ACH is switched on.
  savedBankAccount?: CellularSavedBankAccount | null
  bankOffered?: boolean
  method: PaymentMethodChoice
  disabled?: boolean
  onChange: (method: PaymentMethodChoice) => void
}

interface OptionProps {
  value: PaymentMethodChoice
  checked: boolean
  disabled?: boolean
  onChange: (method: PaymentMethodChoice) => void
}

const Option: FC<OptionProps> = ({ value, checked, disabled, onChange, children }) => {
  const classes = useFormStyles()
  return (
    <label
      className={`${classes.option} ${checked ? classes.optionSelected : ''}`}
      htmlFor={`cellular-card-${value}`}
    >
      <input
        checked={checked}
        className={classes.radio}
        disabled={disabled}
        id={`cellular-card-${value}`}
        name='cellular-payment-method'
        onChange={() => onChange(value)}
        type='radio'
        value={value}
      />
      {children}
    </label>
  )
}

// Whether there is anything to choose between: a saved card, or a bank account.
export const hasPaymentChoices = (props: Pick<PaymentMethodOptionsProps, 'savedCard' | 'bankOffered'>) =>
  Boolean(props.savedCard || props.bankOffered)

// The mock's radio cards: the card on file, the bank account on file, "Use a
// different card", and "Pay from a bank account". The saved card has no
// cardholder name in our data, so its second line shows the expiry only.
export const PaymentMethodOptions: FC<PaymentMethodOptionsProps> = ({
  savedCard,
  savedBankAccount,
  bankOffered,
  method,
  disabled,
  onChange
}) => {
  const classes = useFormStyles()
  const option = (value: PaymentMethodChoice) => ({
    checked: method === value,
    disabled,
    onChange,
    value
  })
  return (
    <div
      className={classes.options}
      role='radiogroup'
    >
      {savedCard && (
        <Option {...option('saved')}>
          <div className={classes.optionText}>
            <p className={classes.optionTitle}>{formatSavedCard(savedCard)}</p>
            <p className={classes.optionSub}>Expires {formatCardExpiry(savedCard)}</p>
          </div>
          <span className={classes.optionSub}>On file</span>
        </Option>
      )}
      {bankOffered && savedBankAccount && (
        <Option {...option('savedBank')}>
          <div className={classes.optionText}>
            <p className={classes.optionTitle}>{formatSavedBankAccount(savedBankAccount)}</p>
            <p className={classes.optionSub}>Bank account (ACH)</p>
          </div>
          <span className={classes.optionSub}>On file</span>
        </Option>
      )}
      <Option {...option('new')}>
        <span className={classes.optionTitle}>
          {savedCard ? 'Use a different card' : 'Pay with a card'}
        </span>
      </Option>
      {bankOffered && (
        <Option {...option('bank')}>
          <div className={classes.optionText}>
            <p className={classes.optionTitle}>Pay from a bank account (ACH)</p>
            <p className={classes.optionSub}>
              Log in to your bank, or enter your routing and account numbers
            </p>
          </div>
        </Option>
      )}
    </div>
  )
}
